import { useEnv } from '@btcdirect/shared/src/composables/util/env';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import WalletAddressValidator from 'multicoin-address-validator';
import { FieldData, ValidationMethod } from 'out-of-the-vue';

const { getString } = useEnv();
const { warn } = useLogger();

type CurrencyRegex = {
    currency: string;
    regexMain: RegExp;
    regexTest: RegExp;
};

const regexes: CurrencyRegex[] = [
    {
        currency: 'TON',
        regexMain: /^[UE]{1}([a-zA-Z0-9\-\_]{47})$/,
        regexTest: /^[k0]{1}([a-zA-Z0-9\-\_]{47})$/
    },
    {
        currency: 'ATOM',
        regexMain: /^cosmos1[a-z0-9]{38}$/,
        regexTest: /^cosmos1[a-z0-9]{38}$/
    },
    {
        currency: 'NEAR',
        regexMain: /^(([a-z0-9]+[-_])*[a-z0-9]+\.)*([a-z0-9]+[-_])*[a-z0-9]+(\.near|(\.[a-z]{2,6}))$|^ed25519:[A-HJ-NP-Za-km-z1-9]{64}$|^[0-9a-fA-F]{64}$/,
        regexTest: /^(([a-z0-9]+[-_])*[a-z0-9]+\.)*([a-z0-9]+[-_])*[a-z0-9]+(\.near|(\.[a-z]{2,6}))$|^ed25519:[A-HJ-NP-Za-km-z1-9]{64}$|^[0-9a-fA-F]{64}$/
    },
    {
        currency: 'APT',
        regexMain: /^0x?[A-Fa-f0-9]{64}$/,
        regexTest: /^0x?[A-Fa-f0-9]{64}$/
    },
    {
        currency: 'SUI',
        regexMain: /^0x[A-Fa-f0-9]{64}$/,
        regexTest: /^0x[A-Fa-f0-9]{64}$/
    },
    {
        currency: 'INJ',
        regexMain: /^inj1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{38}$/,
        regexTest: /^inj1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{38}$/
    },
    {
        currency: 'KAS',
        regexMain: /^kaspa:[ac-hj-np-z02-9]{61}$/,
        regexTest: /^kaspa:[ac-hj-np-z02-9]{61}$/
    }
];

function validateWalletAddressManually(currency: string, address: string, network: string): boolean {
    const regex = regexes.find((r) => r.currency === currency);

    if (!regex) {
        warn(`Currency ${currency} is not supported by the manual validator`);
        return false;
    }

    return network === 'prod' ? regex.regexMain.test(address) : regex.regexTest.test(address);
}

export function getWalletAddressValidation(currency: string): ValidationMethod {
    return {
        name: 'wallet-address',
        validator: (data: FieldData, currency: string) => validateWalletAddress(<string>data.value, currency),
        parameters: [currency]
    };
}

export function validateWalletAddress(address: string, currency: string): boolean {
    const network: string = ['production', 'sandbox'].includes(getString('VITE_ENV', 'development')) ? 'prod' : 'testnet';

    // Convert unsupported ERC-20 currencies to supported currency
    if (['PEPE', 'SHIB', 'FTM', 'POL'].includes(currency)) {
        currency = 'ETH';
    }

    // Support multichain, use last part of the currency
    if (currency.includes('_')) {
        currency = currency.split('_').pop() || currency;
    }

    // These currencies are not supported by 'multicoin-address-validator'
    const unsupportedCurrencies = ['TON', 'NEAR', 'ATOM', 'SUI', 'APT', 'INJ', 'KAS'];

    if (unsupportedCurrencies.includes(currency)) {
        // Manually validate unsupported currencies
        return validateWalletAddressManually(currency, address, network);
    }

    return !!address ? WalletAddressValidator.validate(address, currency, network) : false;
}

export function getDestinationTagValidation(): ValidationMethod {
    return {
        name: 'destination-tag',
        validator: (data: FieldData): boolean => {
            const tag = typeof data.value === 'string' ? Number(data.value) : <number>data.value;

            return !tag || (tag >= 100000000 && tag <= 999999999);
        }
    };
}
